/**
 * components/platform/primitives.tsx
 * ──────────────────────────────────────────────────────────────────────────
 * Cross-platform (web + native) RN primitives that reproduce the SK Platform
 * design system (PLATFROM_DESIGN_INSTRUCTIONS/design/theme.css + data.jsx)
 * pixel-for-pixel. Every value (size, radius, spacing, color) is sourced from
 * the design tokens (utils/platformTokens + theme/colors). Used by all
 * non-auth platform screens.
 *
 * Provided: Tag, StatusBadge, Meter, Panel, FormRow, SettingsHeader,
 *           DataTable, SideDrawer, Divider.
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  Modal as RNModal,
  Platform,
} from 'react-native';

import { useTheme } from '../../context/themeContext';
import { FS, FW, R, SP, Tone, statusTone } from '../../utils/platformTokens';
import Icon from '../icon';

type Colors = ReturnType<typeof useTheme>['theme']['colors'];

/** Resolve a tone to its { fg, bg, border } trio from the theme. */
function toneColors(c: Colors, tone: Tone) {
  switch (tone) {
    case 'success': return { fg: c.success, bg: c.successSoft, border: c.successLine };
    case 'warn': return { fg: c.warn, bg: c.warnSoft, border: c.warnLine };
    case 'danger': return { fg: c.danger, bg: c.dangerSoft, border: c.dangerLine };
    case 'info': return { fg: c.info, bg: c.infoSoft, border: c.infoLine };
    case 'accent': return { fg: c.accentInk, bg: c.accentSoft, border: c.accentLine };
    default: return { fg: c.ink2, bg: c.card, border: c.line };
  }
}

/* ── Tag / Chip ── (.t-chip) ─────────────────────────────────────────────── */
export const Tag: React.FC<{
  tone?: Tone;
  dot?: boolean;
  size?: 'sm' | 'lg';
  children: React.ReactNode;
}> = ({ tone = 'neutral', dot, size = 'sm', children }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const t = toneColors(c, tone);
  const lg = size === 'lg';
  return (
    <View
      style={[
        styles.chip,
        {
          height: lg ? 26 : 22,
          paddingHorizontal: lg ? 12 : 9,
          backgroundColor: t.bg,
          borderColor: t.border,
        },
      ]}
    >
      {dot && <View style={[styles.chipDot, { backgroundColor: t.fg }]} />}
      <Text style={{ color: t.fg, fontSize: lg ? FS.sm : FS.xs, fontWeight: FW.medium }}>
        {children}
      </Text>
    </View>
  );
};

/* ── StatusBadge ── (data.jsx StatusBadge) ───────────────────────────────── */
export const StatusBadge: React.FC<{ status: string; label?: string }> = ({ status, label }) => (
  <Tag dot tone={statusTone(status)}>
    {label || status}
  </Tag>
);

/* ── Meter ── (data.jsx Meter — color shifts near/over capacity) ──────────── */
export const Meter: React.FC<{ used: number; cap: number; unit?: string }> = ({
  used,
  cap,
  unit = '',
}) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const pct = Math.min(100, Math.round((used / cap) * 100));
  const color = pct >= 100 ? c.danger : pct >= 90 ? c.warn : c.accent;
  const u = unit ? ' ' + unit : '';
  return (
    <View style={{ gap: 6 }}>
      <View style={styles.spread}>
        <Text style={{ color: c.ink2, fontSize: FS.sm }}>
          {used.toLocaleString()}{u} / {cap.toLocaleString()}{u}
        </Text>
        <Text style={{ color, fontSize: FS.sm, fontWeight: FW.medium }}>{pct}%</Text>
      </View>
      <View style={[styles.meterTrack, { backgroundColor: c.paper3 }]}>
        <View style={{ width: `${pct}%`, height: '100%', backgroundColor: color, borderRadius: R.full }} />
      </View>
    </View>
  );
};

/* ── Panel ── (data.jsx Panel — a titled card section) ───────────────────── */
export const Panel: React.FC<{
  title?: string;
  sub?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  style?: any;
  bodyStyle?: any;
}> = ({ title, sub, actions, children, style, bodyStyle }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <View style={[styles.card, { backgroundColor: c.card, borderColor: c.line }, style]}>
      {(title || actions) && (
        <View style={[styles.cardHead, { borderColor: c.line }]}>
          <View style={{ flex: 1 }}>
            {!!title && (
              <Text style={{ fontSize: FS.lg, fontWeight: FW.semibold, color: c.ink }}>{title}</Text>
            )}
            {!!sub && <Text style={{ fontSize: FS.sm, color: c.ink3, marginTop: 2 }}>{sub}</Text>}
          </View>
          {!!actions && <View style={styles.cluster}>{actions}</View>}
        </View>
      )}
      <View style={[styles.cardBody, bodyStyle]}>{children}</View>
    </View>
  );
};

/* ── FormRow ── (data.jsx FormRow — 200px label column + field) ──────────── */
export const FormRow: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
  /** stack vertically (mobile) instead of the 200px two-column grid */
  stack?: boolean;
}> = ({ label, hint, children, stack }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <View
      style={[
        styles.formRow,
        { borderColor: c.line, flexDirection: stack ? 'column' : 'row', gap: 16 },
      ]}
    >
      <View style={stack ? undefined : { width: 200 }}>
        <Text style={{ fontSize: FS.md, fontWeight: FW.medium, color: c.ink }}>{label}</Text>
        {!!hint && <Text style={{ fontSize: FS.sm, color: c.ink3, marginTop: 2 }}>{hint}</Text>}
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>{children}</View>
    </View>
  );
};

/* ── SettingsHeader ── (data.jsx SettingsHeader) ─────────────────────────── */
export const SettingsHeader: React.FC<{
  title: string;
  sub?: string;
  action?: React.ReactNode;
}> = ({ title, sub, action }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <View style={[styles.spread, { alignItems: 'flex-start', marginBottom: 20, gap: 16 }]}>
      <View style={{ flex: 1 }}>
        <Text style={{ fontSize: FS.xxl, fontWeight: FW.semibold, color: c.ink, letterSpacing: -0.2 }}>
          {title}
        </Text>
        {!!sub && <Text style={{ color: c.ink3, fontSize: FS.md, marginTop: 4 }}>{sub}</Text>}
      </View>
      {action}
    </View>
  );
};

/* ── Divider ── (.t-divider) ─────────────────────────────────────────────── */
export const Divider: React.FC<{ style?: any }> = ({ style }) => {
  const { theme } = useTheme();
  return <View style={[{ height: 1, backgroundColor: theme.colors.line, marginVertical: 12 }, style]} />;
};

/* ── DataTable ── (.t-table — sticky header, hover/selected rows) ─────────── */
export interface Column<Row> {
  key: string;
  header: string;
  /** fixed/flex width in px; omit to flex:1 */
  width?: number;
  align?: 'left' | 'right' | 'center';
  render: (row: Row, index: number) => React.ReactNode;
}

export function DataTable<Row>({
  columns,
  rows,
  keyExtractor,
  selectedKey,
  minWidth,
  empty,
}: {
  columns: Column<Row>[];
  rows: Row[];
  keyExtractor: (row: Row, index: number) => string;
  selectedKey?: string;
  minWidth?: number;
  empty?: React.ReactNode;
}) {
  const { theme } = useTheme();
  const c = theme.colors;

  const cellStyle = (col: Column<Row>) => ({
    width: col.width,
    flex: col.width ? undefined : 1,
    alignItems:
      col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start',
    justifyContent: 'center' as const,
  });

  const body = (
    <View style={{ minWidth }}>
      {/* head */}
      <View style={[styles.tableHeadRow, { backgroundColor: c.paper2, borderColor: c.line }]}>
        {columns.map((col) => (
          <View key={col.key} style={[styles.tableCell, cellStyle(col) as any]}>
            <Text style={[styles.tableHeadText, { color: c.ink3 }]}>{col.header}</Text>
          </View>
        ))}
      </View>
      {/* rows */}
      {rows.length === 0 && empty ? (
        <View style={{ padding: 24 }}>{empty}</View>
      ) : (
        rows.map((row, i) => {
          const k = keyExtractor(row, i);
          const selected = selectedKey != null && k === selectedKey;
          return (
            <View
              key={k}
              style={[
                styles.tableRow,
                { borderColor: c.line, backgroundColor: selected ? c.accentSoft : c.card },
              ]}
            >
              {columns.map((col) => (
                <View key={col.key} style={[styles.tableCell, cellStyle(col) as any]}>
                  {typeof col.render(row, i) === 'string' || typeof col.render(row, i) === 'number' ? (
                    <Text style={{ color: c.ink, fontSize: FS.md }}>{col.render(row, i) as any}</Text>
                  ) : (
                    col.render(row, i)
                  )}
                </View>
              ))}
            </View>
          );
        })
      )}
    </View>
  );

  // Horizontal scroll container so wide tables never break the page layout.
  return minWidth ? (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      {body}
    </ScrollView>
  ) : (
    body
  );
}

/* ── SideDrawer ── (.t-drawer — right slide-in panel, 440px) ─────────────── */
export const SideDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  title?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
}> = ({ open, onClose, title, footer, children }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  if (!open) return null;
  return (
    <RNModal visible={open} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.drawerBackdrop}>
        <Pressable style={{ flex: 1 }} onPress={onClose} />
        <View style={[styles.drawer, { backgroundColor: c.card, borderColor: c.line }]}>
          {!!title && (
            <View style={[styles.drawerHead, { borderColor: c.line }]}>
              <Text style={{ fontSize: FS.lg, fontWeight: FW.semibold, color: c.ink }}>{title}</Text>
              <Pressable onPress={onClose} hitSlop={8}>
                <Icon name="x" size={18} color={c.ink3} />
              </Pressable>
            </View>
          )}
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 18 }}>
            {children}
          </ScrollView>
          {!!footer && (
            <View style={[styles.drawerFoot, { borderColor: c.line, backgroundColor: c.paper2 }]}>
              {footer}
            </View>
          )}
        </View>
      </View>
    </RNModal>
  );
};

const styles = StyleSheet.create({
  spread: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  cluster: { flexDirection: 'row', flexWrap: 'wrap', gap: SP.s2, alignItems: 'center' },

  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderRadius: R.full,
    borderWidth: 1,
    alignSelf: 'flex-start',
  },
  chipDot: { width: 6, height: 6, borderRadius: 3 },

  meterTrack: { height: 7, borderRadius: R.full, overflow: 'hidden' },

  card: { borderWidth: 1, borderRadius: R.r3, marginBottom: 16 },
  cardHead: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  cardBody: { padding: 16 },

  formRow: { paddingVertical: 16, borderBottomWidth: 1, alignItems: 'flex-start' },

  tableHeadRow: { flexDirection: 'row', borderBottomWidth: 1 },
  tableHeadText: {
    fontSize: FS.xs,
    fontWeight: FW.medium,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  tableRow: { flexDirection: 'row', borderBottomWidth: 1 },
  tableCell: { paddingHorizontal: 12, paddingVertical: 11 },

  drawerBackdrop: { flex: 1, flexDirection: 'row', backgroundColor: 'rgba(16,24,40,0.32)' },
  drawer: {
    width: 440,
    maxWidth: '100%',
    height: '100%',
    borderLeftWidth: 1,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 16px 32px -12px rgba(16,24,40,0.30)' } as any)
      : {}),
  },
  drawerHead: {
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderBottomWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  drawerFoot: {
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderTopWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});
